import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' }
});

api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const tenantId = localStorage.getItem('currentTenantId');
  if (tenantId) config.headers['X-Tenant-ID'] = tenantId;
  return config;
});

// Dashboard
export const getDPDPADashboard = async () => {
  const res = await api.get('/api/dpdpa/dashboard');
  return res.data;
};

// Purposes
export const getPurposes = async () => {
  const res = await api.get('/api/dpdpa/purposes');
  return res.data;
};

export const createPurpose = async (data) => {
  const res = await api.post('/api/dpdpa/purposes', data);
  return res.data;
};

export const updatePurpose = async (id, data) => {
  const res = await api.put(`/api/dpdpa/purposes/${id}`, data);
  return res.data;
};

export const deprecatePurpose = async (id) => {
  const res = await api.delete(`/api/dpdpa/purposes/${id}`);
  return res.data;
};

// Consents
export const getConsents = async (status) => {
  const params = status ? { status } : {};
  const res = await api.get('/api/dpdpa/consents', { params });
  return res.data;
};

export const recordConsent = async (data) => {
  const res = await api.post('/api/dpdpa/consents', data);
  return res.data;
};

export const withdrawConsent = async (id) => {
  const res = await api.put(`/api/dpdpa/consents/${id}/withdraw`);
  return res.data;
};

// Data Subject Requests
export const getDSRs = async (status, requestType) => {
  const params = {};
  if (status) params.status = status;
  if (requestType) params.request_type = requestType;
  const res = await api.get('/api/dpdpa/requests', { params });
  return res.data;
};

export const createDSR = async (data) => {
  const res = await api.post('/api/dpdpa/requests', data);
  return res.data;
};

export const updateDSRStatus = async (id, data) => {
  const res = await api.put(`/api/dpdpa/requests/${id}/status`, data);
  return res.data;
};

// PII Scanner
export const runPIIScan = async () => {
  const res = await api.post('/api/dpdpa/pii/scan');
  return res.data;
};

export const scanCollection = async (collectionName) => {
  const res = await api.post(`/api/dpdpa/pii/scan/${collectionName}`);
  return res.data;
};

export const getPIIScans = async () => {
  const res = await api.get('/api/dpdpa/pii/scans');
  return res.data;
};

export const getLatestScan = async () => {
  const res = await api.get('/api/dpdpa/pii/scans/latest');
  return res.data;
};

export const getPIIDataMap = async () => {
  const res = await api.get('/api/dpdpa/pii/data-map');
  return res.data;
};

// Alert Rules
export const getAlertRules = async () => {
  const res = await api.get('/api/dpdpa/alert-rules');
  return res.data;
};

export const createAlertRule = async (data) => {
  const res = await api.post('/api/dpdpa/alert-rules', data);
  return res.data;
};

export const deleteAlertRule = async (id) => {
  const res = await api.delete(`/api/dpdpa/alert-rules/${id}`);
  return res.data;
};

// Jira
export const getJiraConfig = async () => {
  const res = await api.get('/api/dpdpa/jira/config');
  return res.data;
};

export const saveJiraConfig = async (data) => {
  const res = await api.post('/api/dpdpa/jira/config', data);
  return res.data;
};

export const testJiraConnection = async () => {
  const res = await api.post('/api/dpdpa/jira/test');
  return res.data;
};

// Audit Trail
export const getDPDPAAudit = async (limit = 100) => {
  const res = await api.get('/api/dpdpa/audit', { params: { limit } });
  return res.data;
};

// Notifications
export const getDPDPANotifications = async () => {
  const res = await api.get('/api/dpdpa/notifications');
  return res.data;
};

export const markNotificationRead = async (id) => {
  const res = await api.put(`/api/dpdpa/notifications/${id}/read`);
  return res.data;
};

// Seed
export const seedDefaults = async () => {
  const res = await api.post('/api/dpdpa/seed');
  return res.data;
};

// External DB Scan
export const scanExternalDB = async (data) => {
  const res = await api.post('/api/dpdpa/pii/scan-external', data);
  return res.data;
};

// DB Connections
export const getDBConnections = async () => {
  const res = await api.get('/api/dpdpa/db-connections');
  return res.data;
};

export const createDBConnection = async (data) => {
  const res = await api.post('/api/dpdpa/db-connections', data);
  return res.data;
};

export const deleteDBConnection = async (id) => {
  const res = await api.delete(`/api/dpdpa/db-connections/${id}`);
  return res.data;
};

export const testDBConnection = async (id) => {
  const res = await api.post(`/api/dpdpa/db-connections/${id}/test`);
  return res.data;
};

export const scanDBConnection = async (id) => {
  const res = await api.post(`/api/dpdpa/db-connections/${id}/scan`);
  return res.data;
};

export const checkDBCompliance = async (id) => {
  const res = await api.post(`/api/dpdpa/db-connections/${id}/compliance-check`);
  return res.data;
};

export const checkInternalCompliance = async () => {
  const res = await api.post('/api/dpdpa/pii/compliance-check');
  return res.data;
};

export const getComplianceChecks = async () => {
  const res = await api.get('/api/dpdpa/compliance-checks');
  return res.data;
};

// Consent Banners
export const getBanners = async () => {
  const res = await api.get('/api/dpdpa/banners');
  return res.data;
};

export const createBanner = async (data) => {
  const res = await api.post('/api/dpdpa/banners', data);
  return res.data;
};

export const updateBanner = async (id, data) => {
  const res = await api.put(`/api/dpdpa/banners/${id}`, data);
  return res.data;
};

export const deleteBanner = async (id) => {
  const res = await api.delete(`/api/dpdpa/banners/${id}`);
  return res.data;
};

export const getBannerEmbedCode = async (id) => {
  const res = await api.get(`/api/dpdpa/banners/${id}/embed`);
  return res.data;
};

export const getBannerAnalytics = async (id) => {
  const res = await api.get(`/api/dpdpa/banners/${id}/analytics`);
  return res.data;
};

// Admin DB Config
export const getAdminDBConfig = async () => {
  const res = await api.get('/api/dpdpa/admin/db-config');
  return res.data;
};

export const saveAdminDBConfig = async (data) => {
  const res = await api.post('/api/dpdpa/admin/db-config', data);
  return res.data;
};

export const testAdminDBConfig = async () => {
  const res = await api.post('/api/dpdpa/admin/db-config/test');
  return res.data;
};

// PDF Report
export const downloadComplianceReport = async () => {
  const res = await api.get('/api/dpdpa/report/pdf', { responseType: 'blob' });
  return res.data;
};

// Breach Tracking
export const getBreaches = async (status) => {
  const params = status ? { status } : {};
  const res = await api.get('/api/dpdpa/breaches', { params });
  return res.data;
};

export const createBreach = async (data) => {
  const res = await api.post('/api/dpdpa/breaches', data);
  return res.data;
};

export const getBreach = async (id) => {
  const res = await api.get(`/api/dpdpa/breaches/${id}`);
  return res.data;
};

export const updateBreach = async (id, data) => {
  const res = await api.put(`/api/dpdpa/breaches/${id}`, data);
  return res.data;
};

export const deleteBreach = async (id) => {
  const res = await api.delete(`/api/dpdpa/breaches/${id}`);
  return res.data;
};

export const getBreachStats = async () => {
  const res = await api.get('/api/dpdpa/breaches/stats/summary');
  return res.data;
};

export const downloadDPBLetter = async (id) => {
  const res = await api.get(`/api/dpdpa/breaches/${id}/dpb-letter`, { responseType: 'blob' });
  return res.data;
};

export const downloadPrincipalLetter = async (id, email) => {
  const res = await api.get(`/api/dpdpa/breaches/${id}/principal-letter`, { params: { email }, responseType: 'blob' });
  return res.data;
};

// ========================
// Pre-Assessment
// ========================

export const seedPreAssessmentQuestions = async () => {
  const res = await api.post('/api/dpdpa/pre-assessment/seed-questions');
  return res.data;
};

export const getPreAssessmentQuestions = async (domain) => {
  const params = domain ? { domain } : {};
  const res = await api.get('/api/dpdpa/pre-assessment/questions', { params });
  return res.data;
};

export const createPreAssessmentQuestion = async (data) => {
  const res = await api.post('/api/dpdpa/pre-assessment/questions', data);
  return res.data;
};

export const updatePreAssessmentQuestion = async (id, data) => {
  const res = await api.put(`/api/dpdpa/pre-assessment/questions/${id}`, data);
  return res.data;
};

export const deletePreAssessmentQuestion = async (id) => {
  const res = await api.delete(`/api/dpdpa/pre-assessment/questions/${id}`);
  return res.data;
};

export const getPreAssessments = async () => {
  const res = await api.get('/api/dpdpa/pre-assessment/assessments');
  return res.data;
};

export const createPreAssessment = async (data) => {
  const res = await api.post('/api/dpdpa/pre-assessment/assessments', data);
  return res.data;
};

export const getPreAssessment = async (id) => {
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${id}`);
  return res.data;
};

export const updatePreAssessment = async (id, data) => {
  const res = await api.put(`/api/dpdpa/pre-assessment/assessments/${id}`, data);
  return res.data;
};

export const deletePreAssessment = async (id) => {
  const res = await api.delete(`/api/dpdpa/pre-assessment/assessments/${id}`);
  return res.data;
};

export const getAssessmentQuestionnaire = async (id, domain) => {
  const params = domain ? { domain } : {};
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${id}/questionnaire`, { params });
  return res.data;
};

export const submitAnswer = async (assessmentId, data) => {
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/answers`, data);
  return res.data;
};

export const submitAnswersBulk = async (assessmentId, answers) => {
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/answers/bulk`, answers);
  return res.data;
};

export const getAssessmentFindings = async (assessmentId, riskLevel, domain) => {
  const params = {};
  if (riskLevel) params.risk_level = riskLevel;
  if (domain) params.domain = domain;
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/findings`, { params });
  return res.data;
};

export const updateFinding = async (assessmentId, findingId, data) => {
  const res = await api.put(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/findings/${findingId}`, data);
  return res.data;
};

export const uploadEvidence = async (assessmentId, file, questionId = '', description = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('question_id', questionId);
  formData.append('description', description);
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/evidence`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });
  return res.data;
};

export const getAssessmentEvidence = async (assessmentId, questionId) => {
  const params = questionId ? { question_id: questionId } : {};
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/evidence`, { params });
  return res.data;
};

export const deleteEvidence = async (assessmentId, evidenceId) => {
  const res = await api.delete(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/evidence/${evidenceId}`);
  return res.data;
};

export const getPreAssessmentObligations = async () => {
  const res = await api.get('/api/dpdpa/pre-assessment/obligations');
  return res.data;
};

// Domain Scanner
export const runDomainScan = async (assessmentId, data) => {
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/scans`, data);
  return res.data;
};

export const getAssessmentScans = async (assessmentId) => {
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/scans`);
  return res.data;
};

export const getScanDetail = async (assessmentId, scanId) => {
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/scans/${scanId}`);
  return res.data;
};

export const deleteScan = async (assessmentId, scanId) => {
  const res = await api.delete(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/scans/${scanId}`);
  return res.data;
};

// Monitoring
export const createMonitoring = async (assessmentId, data) => {
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/monitoring`, data);
  return res.data;
};

export const getMonitoring = async (assessmentId) => {
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/monitoring`);
  return res.data;
};

export const updateMonitoring = async (assessmentId, monitorId, data) => {
  const res = await api.put(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/monitoring/${monitorId}`, data);
  return res.data;
};

export const deleteMonitoring = async (assessmentId, monitorId) => {
  const res = await api.delete(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/monitoring/${monitorId}`);
  return res.data;
};

export const runMonitoringScan = async (assessmentId, monitorId) => {
  const res = await api.post(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/monitoring/${monitorId}/run`);
  return res.data;
};

// PDF Report
export const downloadPreAssessmentReport = async (assessmentId) => {
  const res = await api.get(`/api/dpdpa/pre-assessment/assessments/${assessmentId}/report/pdf`, { responseType: 'blob' });
  return res.data;
};

// DPO Dashboard
export const getDPODashboard = async () => {
  const res = await api.get('/api/dpdpa/dpo-dashboard');
  return res.data;
};

// Breach Letters (versioned)
export const generateBreachLetter = async (breachId, data) => {
  const res = await api.post(`/api/dpdpa/breaches/${breachId}/letters`, data);
  return res.data;
};

export const getBreachLetters = async (breachId) => {
  const res = await api.get(`/api/dpdpa/breaches/${breachId}/letters`);
  return res.data;
};

export const downloadBreachLetter = async (breachId, letterId) => {
  const res = await api.get(`/api/dpdpa/breaches/${breachId}/letters/${letterId}/download`, { responseType: 'blob' });
  return res.data;
};

export const submitLetterForReview = async (breachId, letterId) => {
  const res = await api.post(`/api/dpdpa/breaches/${breachId}/letters/${letterId}/submit-review`);
  return res.data;
};

export const approveBreachLetter = async (breachId, letterId, notes = '') => {
  const res = await api.post(`/api/dpdpa/breaches/${breachId}/letters/${letterId}/approve`, { notes });
  return res.data;
};

export const rejectBreachLetter = async (breachId, letterId, notes = '') => {
  const res = await api.post(`/api/dpdpa/breaches/${breachId}/letters/${letterId}/reject`, { notes });
  return res.data;
};

export const markLetterSent = async (breachId, letterId, notes = '') => {
  const res = await api.post(`/api/dpdpa/breaches/${breachId}/letters/${letterId}/mark-sent`, { notes });
  return res.data;
};

// ========================
// Breach Detection
// ========================
export const getDetectionRuleTypes = async () => {
  const res = await api.get('/api/dpdpa/advanced/breach-detection/rule-types');
  return res.data;
};
export const getDetectionRules = async () => {
  const res = await api.get('/api/dpdpa/advanced/breach-detection/rules');
  return res.data;
};
export const createDetectionRule = async (data) => {
  const res = await api.post('/api/dpdpa/advanced/breach-detection/rules', data);
  return res.data;
};
export const updateDetectionRule = async (id, data) => {
  const res = await api.put(`/api/dpdpa/advanced/breach-detection/rules/${id}`, data);
  return res.data;
};
export const deleteDetectionRule = async (id) => {
  const res = await api.delete(`/api/dpdpa/advanced/breach-detection/rules/${id}`);
  return res.data;
};
export const seedDetectionRules = async () => {
  const res = await api.post('/api/dpdpa/advanced/breach-detection/seed-rules');
  return res.data;
};
export const runBreachDetectionScan = async () => {
  const res = await api.post('/api/dpdpa/advanced/breach-detection/run-scan');
  return res.data;
};
export const getDetectionAlerts = async (status) => {
  const params = status ? { status } : {};
  const res = await api.get('/api/dpdpa/advanced/breach-detection/alerts', { params });
  return res.data;
};
export const updateAlertStatus = async (id, data) => {
  const res = await api.put(`/api/dpdpa/advanced/breach-detection/alerts/${id}/status`, data);
  return res.data;
};

// ========================
// Legacy Consent Migration
// ========================
export const downloadMigrationTemplate = async () => {
  const res = await api.get('/api/dpdpa/advanced/migration/csv-template', { responseType: 'blob' });
  return res.data;
};
export const importLegacyConsents = async (file, gracePeriodDays = 90) => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('grace_period_days', gracePeriodDays);
  const res = await api.post('/api/dpdpa/advanced/migration/import-consents', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  return res.data;
};
export const getMigrationHistory = async () => {
  const res = await api.get('/api/dpdpa/advanced/migration/history');
  return res.data;
};
export const getReconsentStatus = async () => {
  const res = await api.get('/api/dpdpa/advanced/migration/reconsent-status');
  return res.data;
};
export const refreshLegacyConsent = async (id) => {
  const res = await api.put(`/api/dpdpa/advanced/migration/consents/${id}/refresh`);
  return res.data;
};

// ========================
// Data Residency
// ========================
export const getDataResidency = async () => {
  const res = await api.get('/api/dpdpa/advanced/data-residency');
  return res.data;
};
export const updateDataResidency = async (data) => {
  const res = await api.put('/api/dpdpa/advanced/data-residency', data);
  return res.data;
};

// ========================
// NIST Privacy Framework Mapping
// ========================
export const getNISTMapping = async () => {
  const res = await api.get('/api/dpdpa/advanced/nist-mapping');
  return res.data;
};

// ========================
// Data Flow Mapping
// ========================
export const getDataFlowMaps = async () => {
  const res = await api.get('/api/dpdpa/advanced/dataflow/maps');
  return res.data;
};
export const createDataFlowMap = async (data) => {
  const res = await api.post('/api/dpdpa/advanced/dataflow/maps', data);
  return res.data;
};
export const getDataFlowMap = async (id) => {
  const res = await api.get(`/api/dpdpa/advanced/dataflow/maps/${id}`);
  return res.data;
};
export const updateDataFlowMap = async (id, data) => {
  const res = await api.put(`/api/dpdpa/advanced/dataflow/maps/${id}`, data);
  return res.data;
};
export const deleteDataFlowMap = async (id) => {
  const res = await api.delete(`/api/dpdpa/advanced/dataflow/maps/${id}`);
  return res.data;
};

// ========================
// DLP Platform Connector
// ========================
export const getDLPPlatforms = async () => {
  const res = await api.get('/api/dpdpa/advanced/dlp/platforms');
  return res.data;
};
export const getDLPConnections = async () => {
  const res = await api.get('/api/dpdpa/advanced/dlp/connections');
  return res.data;
};
export const createDLPConnection = async (data) => {
  const res = await api.post('/api/dpdpa/advanced/dlp/connections', data);
  return res.data;
};
export const deleteDLPConnection = async (id) => {
  const res = await api.delete(`/api/dpdpa/advanced/dlp/connections/${id}`);
  return res.data;
};
export const testDLPConnection = async (id) => {
  const res = await api.post(`/api/dpdpa/advanced/dlp/connections/${id}/test`);
  return res.data;
};
export const syncDLPFindings = async (id) => {
  const res = await api.post(`/api/dpdpa/advanced/dlp/connections/${id}/sync`);
  return res.data;
};
export const importDLPFindingsJSON = async (connId, findings) => {
  const res = await api.post(`/api/dpdpa/advanced/dlp/connections/${connId}/import-json`, { findings });
  return res.data;
};
export const getDLPFindings = async (connectionId, severity, platform) => {
  const params = {};
  if (connectionId) params.connection_id = connectionId;
  if (severity) params.severity = severity;
  if (platform) params.platform = platform;
  const res = await api.get('/api/dpdpa/advanced/dlp/findings', { params });
  return res.data;
};
export const getDLPDashboard = async () => {
  const res = await api.get('/api/dpdpa/advanced/dlp/dashboard');
  return res.data;
};
